import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { VerificationEntry } from '../types';
import PermissionGuard from './PermissionGuard';

// Import sub-components
import DashboardHeader from './dashboard/DashboardHeader';
import StatsSummary from './dashboard/StatsSummary';
import SearchAndFilters from './dashboard/SearchAndFilters';
import VerificationTable from './dashboard/VerificationTable';
import DetailModal from './dashboard/DetailModal';

interface VerificationsProps {
	isAdmin?: boolean;
	onLogout?: () => void;
}

const parseIdentityEntry = (entry: any): VerificationEntry => ({
	id: entry.id,
	ghanaCardId: entry.national_id || entry.card_id || 'Unknown',
	firstName: entry.forenames,
	lastName: entry.surname,
	dateOfBirth: entry.birth_date,
	status: entry.final_status || 'pending',
	card_valid_from: entry.card_valid_from,
	card_valid_to: entry.card_valid_to,
	timestamp: new Date(entry.created_at),
	selfie_image: entry.selfie_image,
	selfie_image_url: entry.selfie_image_url,
	birth_town: entry.birth_town,
	birth_region: entry.birth_region,
	gps_address: entry.gps_address,
	address: entry.address,
	town: entry.town,
	region: entry.region,
	country: entry.country,
	biometricFaceImage: entry.biometric_face_image,
	biometric_face_image_url: entry.biometric_face_image_url,
});

const Verifications: React.FC<VerificationsProps> = () => {
	const navigate = useNavigate();
	const [entries, setEntries] = useState<VerificationEntry[]>([]);
	const [filteredEntries, setFilteredEntries] = useState<VerificationEntry[]>([]);
	const [selectedEntry, setSelectedEntry] = useState<VerificationEntry | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [dateFilter, setDateFilter] = useState('all');
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const ITEMS_PER_PAGE = 50;
	const [currentPage, setCurrentPage] = useState(1);
	const [statusCounts, setStatusCounts] = useState({
		verified: 0,
		failed: 0,
		pending: 0,
	});
	const [allProcessedIdentities, setAllProcessedIdentities] = useState<VerificationEntry[]>([]);

	// Process submissions into unique identities with final status (success overrides failure)
	const processSubmissionsIntoIdentities = useCallback(
		(submissions: any[]): VerificationEntry[] => {
			const identityMap = new Map<string, any[]>();

			submissions.forEach(submission => {
				if (submission.national_id) {
					if (!identityMap.has(submission.national_id)) {
						identityMap.set(submission.national_id, []);
					}
					identityMap.get(submission.national_id)!.push(submission);
				}
			});

			const uniqueIdentities: VerificationEntry[] = [];

			identityMap.forEach((identitySubmissions) => {
				identitySubmissions.sort(
					(a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				);

				const hasVerifiedAttempt = identitySubmissions.some(sub => sub.status === 'verified');
				const latestSubmission = identitySubmissions[0];
				const finalStatus = hasVerifiedAttempt ? 'verified' : latestSubmission.status;

				uniqueIdentities.push(
					parseIdentityEntry({
						...latestSubmission,
						final_status: finalStatus,
					})
				);
			});

			uniqueIdentities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

			return uniqueIdentities;
		},
		[]
	);

	const loadAllSubmissionsWithEssentialColumns = useCallback(async () => {
		const BATCH_SIZE = 1000;
		let allSubmissions: any[] = [];
		let offset = 0;
		let hasMore = true;

		console.log('Loading all submissions with essential columns...');

		while (hasMore) {
			const { data, error } = await supabase
				.from('submissions')
				.select('id, national_id, card_id, forenames, surname, birth_date, status, created_at')
				.not('national_id', 'is', null)
				.order('created_at', { ascending: false })
				.range(offset, offset + BATCH_SIZE - 1);

			if (error) {
				console.error('Error fetching submissions batch:', error);
				throw error;
			}

			if (data && data.length > 0) {
				allSubmissions = [...allSubmissions, ...data];
				offset += BATCH_SIZE;
				hasMore = data.length === BATCH_SIZE;
				console.log(`Batch ${Math.ceil(offset / BATCH_SIZE)}: Loaded ${data.length} records. Total: ${allSubmissions.length}`);
			} else {
				hasMore = false;
			}
		}

		console.log(`✓ Loaded ALL ${allSubmissions.length} submissions with essential columns`);
		return allSubmissions;
	}, []);

	const loadInitialEntries = useCallback(async () => {
		try {
			setIsLoading(true);
			setIsLoadingMetrics(true);

			const allSubmissions = await loadAllSubmissionsWithEssentialColumns();

			console.log(`Processing ${allSubmissions.length} submissions into unique identities...`);

			const uniqueIdentities = processSubmissionsIntoIdentities(allSubmissions);

			console.log(`✓ Created ${uniqueIdentities.length} unique identities from ${allSubmissions.length} submissions`);

			setTotalCount(uniqueIdentities.length);
			setStatusCounts({
				verified: uniqueIdentities.filter(i => i.status === 'verified').length,
				failed: uniqueIdentities.filter(i => i.status === 'failed').length,
				pending: uniqueIdentities.filter(i => i.status === 'pending').length,
			});
			setAllProcessedIdentities(uniqueIdentities);
			setEntries(uniqueIdentities);
			setIsLoadingMetrics(false);
		} catch (err) {
			console.error('Error loading initial identities:', err);
		} finally {
			setIsLoading(false);
			setIsLoadingMetrics(false);
		}
	}, [loadAllSubmissionsWithEssentialColumns, processSubmissionsIntoIdentities]);

	// Refetch a single identity by reprocessing submissions for that national_id
	const refetchIdentity = useCallback(async (nationalId: string) => {
		try {
			const { data, error } = await supabase
				.from('submissions')
				.select('id, national_id, card_id, forenames, surname, birth_date, status, created_at')
				.eq('national_id', nationalId)
				.order('created_at', { ascending: false });

			if (error || !data || data.length === 0) return;

			const hasVerifiedAttempt = data.some(sub => sub.status === 'verified');
			const latestSubmission = data[0];
			const finalStatus = hasVerifiedAttempt ? 'verified' : latestSubmission.status;

			const updatedEntry = parseIdentityEntry({
				...latestSubmission,
				final_status: finalStatus,
			});

			setEntries(prev => {
				const existingIndex = prev.findIndex(entry => entry.ghanaCardId === nationalId);
				let updated: VerificationEntry[];

				if (existingIndex >= 0) {
					updated = [...prev];
					updated[existingIndex] = updatedEntry;
				} else {
					updated = [updatedEntry, ...prev];
				}

				setStatusCounts({
					verified: updated.filter(i => i.status === 'verified').length,
					failed: updated.filter(i => i.status === 'failed').length,
					pending: updated.filter(i => i.status === 'pending').length,
				});
				setTotalCount(updated.length);

				return updated;
			});

			setAllProcessedIdentities(prev => {
				const existingIndex = prev.findIndex(entry => entry.ghanaCardId === nationalId);

				if (existingIndex >= 0) {
					const updated = [...prev];
					updated[existingIndex] = updatedEntry;
					return updated;
				}

				return [updatedEntry, ...prev];
			});
		} catch (err) {
			console.error('Error refetching identity:', err);
		}
	}, []);

	// Initialize data loading and set up real-time subscription
	useEffect(() => {
		loadInitialEntries();

		const subscription = supabase
			.channel('identity_changes')
			.on('postgres_changes', { event: '*', schema: 'public', table: 'submissions' }, payload => {
				const { eventType, new: newRecord } = payload;

				if (eventType === 'INSERT' || eventType === 'UPDATE') {
					if (newRecord?.national_id) {
						refetchIdentity(newRecord.national_id);
					}
				}
			})
			.subscribe();

		const refreshInterval = setInterval(() => {
			console.log('Auto-refreshing data (5-minute interval)...');
			loadInitialEntries();
		}, 5 * 60 * 1000);

		return () => {
			supabase.removeChannel(subscription);
			clearInterval(refreshInterval);
		};
	}, [loadInitialEntries, refetchIdentity]);

	// Apply filters to entries
	useEffect(() => {
		let filtered = entries;

		if (searchTerm) {
			filtered = filtered.filter(
				entry =>
					entry.ghanaCardId.toLowerCase().includes(searchTerm.toLowerCase()) ||
					`${entry.firstName || ''} ${entry.lastName || ''}`
						.toLowerCase()
						.includes(searchTerm.toLowerCase())
			);
		}

		if (statusFilter !== 'all') {
			filtered = filtered.filter(entry => entry.status === statusFilter);
		}

		if (dateFilter !== 'all') {
			const now = new Date();
			const filterDate = new Date();

			switch (dateFilter) {
				case 'today':
					filterDate.setHours(0, 0, 0, 0);
					filtered = filtered.filter(entry => entry.timestamp >= filterDate);
					break;
				case 'week':
					filterDate.setDate(now.getDate() - 7);
					filtered = filtered.filter(entry => entry.timestamp >= filterDate);
					break;
				case 'month':
					filterDate.setMonth(now.getMonth() - 1);
					filtered = filtered.filter(entry => entry.timestamp >= filterDate);
					break;
				case 'custom':
					if (customDateFrom) {
						const fromDate = new Date(customDateFrom);
						fromDate.setHours(0, 0, 0, 0);
						filtered = filtered.filter(entry => entry.timestamp >= fromDate);
					}
					if (customDateTo) {
						const toDate = new Date(customDateTo);
						toDate.setHours(23, 59, 59, 999);
						filtered = filtered.filter(entry => entry.timestamp <= toDate);
					}
					break;
			}
		}

		setFilteredEntries(filtered);
	}, [entries, searchTerm, statusFilter, dateFilter, customDateFrom, customDateTo]);

	// Ensure no duplicates in displayed entries (safety)
	const displayedEntries = filteredEntries.filter(
		(entry, index, arr) => arr.findIndex(e => e.ghanaCardId === entry.ghanaCardId) === index
	);

	// Stats for the StatsSummary component - use counts from view
	const totalVerifications = totalCount;
	const verifiedCount = statusCounts.verified;
	const failedCount = statusCounts.failed;
	const pendingCount = statusCounts.pending;

	const formatDate = (date: Date) =>
		new Intl.DateTimeFormat('en-GB', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(date);

	// Fetch full entry details with all columns (images, addresses, etc.)
	const fetchFullEntryDetails = async (entryId: string) => {
		try {
			const { data, error } = await supabase
				.from('submissions')
				.select('*')
				.eq('id', entryId)
				.single();

			if (error) {
				console.error('Error fetching full entry details:', error);
				return null;
			}

			return data;
		} catch (err) {
			console.error('Error in fetchFullEntryDetails:', err);
			return null;
		}
	};

	const handleViewDetails = async (entry: VerificationEntry) => {
		setIsLoadingDetails(true);

		// Fetch full data with all columns
		const fullData = await fetchFullEntryDetails(entry.id);

		if (fullData) {
			// Parse the full data and show in modal
			const completeEntry = parseIdentityEntry(fullData);
			setSelectedEntry(completeEntry);
		} else {
			// Fallback to showing partial data
			setSelectedEntry(entry);
		}

		setIsLoadingDetails(false);
	};

	const handleCloseModal = () => {
		setSelectedEntry(null);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setCurrentPage(1); // Reset to first page on search
	};

	const handleStatusFilterChange = (status: string) => {
		setStatusFilter(status);
		setCurrentPage(1); // Reset to first page on filter
	};

	const handleDateFilterChange = (date: string) => {
		setDateFilter(date);
		setCurrentPage(1); // Reset to first page on filter
		if (date !== 'custom') {
			setCustomDateFrom('');
			setCustomDateTo('');
		}
	};

	const handleCustomDateFromChange = (date: string) => {
		setCustomDateFrom(date);
		setDateFilter('custom');
		setCurrentPage(1); // Reset to first page on filter
	};

	const handleCustomDateToChange = (date: string) => {
		setCustomDateTo(date);
		setDateFilter('custom');
		setCurrentPage(1); // Reset to first page on filter
	};

	const clearFilters = () => {
		setSearchTerm('');
		setStatusFilter('all');
		setDateFilter('all');
		setCustomDateFrom('');
		setCustomDateTo('');
		setCurrentPage(1); // Reset to first page
	};

	// Pagination logic
	const totalPages = Math.ceil(displayedEntries.length / ITEMS_PER_PAGE);
	const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIdx = startIdx + ITEMS_PER_PAGE;
	const paginatedEntries = displayedEntries.slice(startIdx, endIdx);

	const goToPage = (page: number) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	return (
		<PermissionGuard permission="submissions.read">
			<div className="container mx-auto px-4 py-8">
				{/* HEADER */}
				<DashboardHeader
					title="Identity Verifications"
					description="Unique identity verification status (one per Ghana Card)"
					buttonLabel="New Verification"
					buttonPermission="submissions.create"
					onButtonAction={() => navigate('/verify')}
				/>

				{/* STATS */}
				<StatsSummary
					total={totalVerifications}
					verified={verifiedCount}
					failed={failedCount}
					pending={pendingCount}
					isLoading={isLoadingMetrics}
				/>

				{/* SEARCH AND FILTERS */}
				<div className="mx-auto">
					<SearchAndFilters
						searchTerm={searchTerm}
						statusFilter={statusFilter}
						dateFilter={dateFilter}
						customDateFrom={customDateFrom}
						customDateTo={customDateTo}
						onSearchChange={handleSearchChange}
						onStatusFilterChange={handleStatusFilterChange}
						onDateFilterChange={handleDateFilterChange}
						onCustomDateFromChange={handleCustomDateFromChange}
						onCustomDateToChange={handleCustomDateToChange}
						onClearFilters={clearFilters}
						hasActiveFilters={
							searchTerm !== '' ||
							statusFilter !== 'all' ||
							dateFilter !== 'all' ||
							customDateFrom !== '' ||
							customDateTo !== ''
						}
					/>
				</div>

				{/* TABLE */}
				<div className="mx-auto mb-6">
					{isLoading ? (
						<div className="bg-white rounded-xl shadow-sm overflow-x-auto">
							<table className="min-w-full text-sm">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
											Ghana Card ID
										</th>
										<th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
											Name
										</th>
										<th className="px-3 py-2 text-center font-medium text-gray-500 uppercase">
											Status
										</th>
										<th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
											Date
										</th>
										<th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">
											Action
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{Array.from({ length: 5 }).map((_, index) => (
										<tr key={`skeleton-${index}`}>
											<td className="px-3 py-3">
												<div className="h-4 bg-gray-200 rounded animate-pulse w-28"></div>
											</td>
											<td className="px-3 py-3">
												<div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
											</td>
											<td className="px-3 py-3 text-center">
												<div className="h-4 bg-gray-200 rounded animate-pulse w-16 mx-auto"></div>
											</td>
											<td className="px-3 py-3">
												<div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
											</td>
											<td className="px-3 py-3">
												<div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : filteredEntries.length === 0 ? (
						<div className="bg-white p-6 rounded-xl shadow-sm text-center">
							<p className="text-gray-600">No unique identities found</p>
						</div>
					) : (
						<>
							<VerificationTable
								entries={paginatedEntries}
								onViewDetails={handleViewDetails}
								formatDate={formatDate}
							/>

							{/* PAGINATION CONTROLS */}
							{totalPages > 1 && (
								<div className="mt-6 flex items-center justify-center gap-2">
									<button
										onClick={() => goToPage(currentPage - 1)}
										disabled={currentPage === 1}
										className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Previous
									</button>

									<div className="flex gap-1">
										{Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
											let pageNum;
											if (totalPages <= 7) {
												pageNum = i + 1;
											} else if (currentPage <= 4) {
												pageNum = i + 1;
											} else if (currentPage >= totalPages - 3) {
												pageNum = totalPages - 6 + i;
											} else {
												if (i === 0) pageNum = 1;
												else if (i === 1) pageNum = currentPage - 1;
												else if (i === 2) pageNum = currentPage;
												else if (i === 3) pageNum = currentPage + 1;
												else pageNum = totalPages;
											}

											return (
												<button
													key={pageNum}
													onClick={() => goToPage(pageNum)}
													className={`px-3 py-2 rounded-lg ${
														currentPage === pageNum
															? 'bg-ghana-red text-white'
															: 'bg-white border border-gray-300 hover:bg-gray-50'
													}`}
												>
													{pageNum}
												</button>
											);
										})}
									</div>

									<button
										onClick={() => goToPage(currentPage + 1)}
										disabled={currentPage === totalPages}
										className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Next
									</button>
								</div>
							)}

							{/* RESULTS INFO */}
							<div className="mt-4 text-center text-sm text-gray-600">
								{searchTerm || statusFilter !== 'all' || dateFilter !== 'all' ? (
									<>
										Showing {startIdx + 1}-{Math.min(endIdx, displayedEntries.length)} of {displayedEntries.length} unique identities (filtered)
										<br />
										<span className="text-gray-500">Page {currentPage} of {totalPages}</span>
									</>
								) : (
									<>
										Showing {startIdx + 1}-{Math.min(endIdx, allProcessedIdentities.length)} of {allProcessedIdentities.length} unique identities
										<br />
										<span className="text-gray-500">Page {currentPage} of {totalPages}</span>
									</>
								)}
							</div>
						</>
					)}
				</div>

				{/* DETAIL MODAL */}
				{selectedEntry && (
					<DetailModal
						entry={selectedEntry}
						onClose={handleCloseModal}
						formatDate={formatDate}
						isLoading={isLoadingDetails}
					/>
				)}
			</div>
		</PermissionGuard>
	);
};

export default Verifications;
